/**
 * The shop's design vocabulary: one list of named roles, for both themes.
 *
 * Before this, the values lived in three places that did not know about each
 * other — `createTheme`'s palette, a private `SummaryTokens` set written for
 * the order summary, and hex literals pasted across the components.
 *
 * Counted honestly, the interface chrome (excluding the greeting-card and
 * calendar palettes, the ship illustration and test fixtures, which are
 * product content and belong as literals) holds **115 distinct colours across
 * 35 files**. The ones that matter:
 *
 *   #E65840 17×  the add-to-basket button — the single most important
 *   #C94E35  4×  element in the shop, and its hover, named nowhere
 *   #3AAFB9  9×  a second teal, competing with the brand
 *   #2D6F7A  4×  a third
 *   #2f82f1  2×  theme `secondary`, which nothing much uses
 *
 * An earlier draft of this comment claimed "four blues in active use"
 * including #4f8ef7 and #1098ad. That was wrong and is corrected here:
 * #4f8ef7 is a doc-comment example in greeting-card/color-utils.ts plus its
 * test fixtures, and #1098ad is a calendar gradient preset. Neither is
 * chrome. The shop was more consistent than the first measurement suggested.
 *
 * The names come from the order summary's own palette, which was the best
 * thought-out thing in the codebase and was reaching only one page. They
 * describe a photographic print, which is a vocabulary the whole shop can
 * reason in: ink on paper, mounted on a mat, with mist between and sepia for
 * warmth. `action` is the one addition — the orange that was already the buy
 * button, finally given a name and a rule.
 *
 * The rule, which is the point of all of this:
 *
 *   brand  is identity — links, focus, the price. Never a full-width wash.
 *   action is the verb  — one per screen, on the thing you want clicked.
 *
 * Values are unchanged from what shipped, so adopting this file is invisible:
 * the migration is about where a colour comes from, not what it is.
 */

export interface DesignTokens {
  /** Body text. A near-black biased blue-green, so it sits with the brand. */
  ink: string;
  /** Secondary text. A defined step, not `opacity: 0.6` on the same ink. */
  ink2: string;
  /** Tertiary text — captions, hints. The last legible step. */
  ink3: string;
  /** The page ground. */
  paper: string;
  /**
   * A print's mat board: photo mounts and product previews. Stays cream in
   * dark mode, so never put `ink` on it there — use `surface`.
   */
  mat: string;
  /**
   * Controls and cards that carry text: option tiles, steppers, sheets.
   * White in light mode (the colour they had on `mat`), a raised dark in dark
   * mode, where `mat`'s cream under the light ink hid every label.
   */
  surface: string;
  /** Identity. */
  brand: string;
  /** A tint of the brand, for marking one element — never a whole panel. */
  brandSoft: string;
  /** The verb. One per screen. */
  action: string;
  /** Hover/pressed state for `action`. */
  actionHover: string;
  /** Eyebrow labels only. A toned print's warmth, in small doses. */
  sepia: string;
  /** Hairlines and dividers. Never a border you notice. */
  mist: string;
  /** Semantic confirmation — a tick. Deliberately not an accent. */
  ok: string;
}

export const LIGHT_TOKENS: DesignTokens = {
  ink: "#16232B",
  ink2: "#4A5860",
  ink3: "#7B878D",
  paper: "#FBFAF8",
  mat: "#FFFFFF",
  surface: "#FFFFFF",
  brand: "#109BC5",
  brandSoft: "#E7F4F9",
  action: "#E65840",
  actionHover: "#D14A33",
  sepia: "#A8794F",
  mist: "#E6E9EA",
  ok: "#1F9254",
};

/**
 * `mat` stays paper-white in dark mode on purpose, the way a print's actual
 * mat board would under any room lighting — inherited from the summary's
 * original set, where the reasoning was written down and is still right.
 */
export const DARK_TOKENS: DesignTokens = {
  // `brand` is exactly what MUI derived before this file existed —
  // lighten("#109bc5", 0.2) = rgb(63, 175, 208) — so adopting the tokens
  // changes nothing on screen. Pick a different dark brand deliberately, in
  // the design step, not as a side effect of the migration.
  ink: "#F2EEE8",
  ink2: "#B9B3AB",
  ink3: "#8A8480",
  paper: "#14181B",
  mat: "#F4F0E9",
  surface: "#1E2529",
  brand: "#3FAFD0",
  brandSoft: "#16323D",
  action: "#F0765F",
  actionHover: "#E65840",
  sepia: "#CBA173",
  mist: "rgba(255,255,255,0.14)",
  ok: "#4FBF80",
};

/** The spacing scale, in px. MUI's unit is 8, so these are its multiples. */
export const SPACE = {
  s1: 4,
  s2: 8,
  s3: 12,
  s4: 16,
  s5: 24,
  s6: 32,
  s7: 48,
  s8: 64,
} as const;

/** Controls, cards, and prices, in that order. */
export const RADIUS = {
  control: 6,
  card: 12,
  pill: 999,
} as const;

/**
 * The minimum touch target, in px (Apple HIG and WCAG 2.5.8 both land here).
 *
 * Named because the shop currently has 30px quantity and zoom controls, and a
 * number with a name is harder to undercut than one typed inline.
 */
export const MIN_TAP_TARGET = 44;
